import type {
  CarePlanGetResponse,
  CreateCarePlanRequest,
} from '@quromedical/models';
import '@quromedical/utils';
import { first } from '@quromedical/utils';

import {
  type AssertRule,
  type AssertRules,
  compose,
  resolveConstant,
} from '../composer';

type CarePlanRuleName = 'new-care-plan';

type CarePlanParams = {
  rule: CarePlanRuleName;
  carePlans: CarePlanGetResponse[];
  newCarePlan?: CreateCarePlanRequest;
};

type AssertCarePlanRule = AssertRule<CarePlanParams>;

const assertCanCreateAdmissionCarePlan: AssertCarePlanRule = ({
  carePlans,
  newCarePlan,
}) => {
  if (carePlans.length === 0) {
    return undefined;
  }
  const hasActiveCarePlan = carePlans.some((c) => c.status === 'active');

  if (hasActiveCarePlan) {
    return {
      type: 'error',
      messages: [
        `Cannot create a new care plan because there is an active one`,
      ],
    };
  }

  const onHoldCarePlans = carePlans.filter((c) => c.status === 'on-hold');

  const canCreateCarePlan = onHoldCarePlans.every(
    (c) => first(c.instantiatesCanonical) !== newCarePlan?.planDefinition,
  );

  if (canCreateCarePlan) {
    return undefined;
  }

  return {
    type: 'error',
    messages: [
      `Cannot create a care plan with the same name as the previously paused care plan.`,
    ],
  };
};

const CarePlanAssertions: AssertRules<CarePlanRuleName, CarePlanParams> = {
  'new-care-plan': [assertCanCreateAdmissionCarePlan],
};

/**
 * a function that takes in a set of parameters and based on that determines if a new care plan can be created
 */
export const validateCarePlanRequest = compose({
  getRuleName: ({ rule }) => rule,
  assertions: CarePlanAssertions,
  defaultAssertions: [],
  defaultResolver: resolveConstant<true>(true),
});
